import Loading from 'core-system/Loading'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import React, { useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { MyCommuter, MyCommuterArchived } from 'redux/employee/employeeTypes'
import { Worksite } from 'redux/employer/employerTypes'
import styled from 'styled-components'
import MyCommuterDetailsModal from './CommutersInfoDrawer'
import Avatar from 'core-system/Avatar/Avatar'

const Body = styled.div`
  margin: 1.5rem 0 6rem;
`

const UserContainer = styled.div`
  display: flex;
`

const NameContainer = styled.div`
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`

const getTableRows = (
  filteredCommuters: MyCommuter[],
  sortByFilter: string,
  worksites: Worksite[],
  onRowClick: (id: string) => void
) => {
  return filteredCommuters
    .sort((a, b) =>
      sortByFilter === 'desc'
        ? new Date(b.archived).getTime() - new Date(a.archived).getTime()
        : new Date(a.archived).getTime() - new Date(b.archived).getTime()
    )
    .map((commuter, idx) => {
      const matchingWorksite = worksites.find(
        (wrk) => wrk.id === commuter.worksiteId
      )
      return (
        <TableRow key={idx} isClickable={true}>
          <TableRowCell onClick={() => onRowClick(commuter.id)}>
            <UserContainer>
              <Avatar name={commuter.name} size='medium' />
              <NameContainer>
                <Text variant='action4' hideOverflow>
                  {commuter.name}
                </Text>
                <Text variant='body2' textColor={palette.text.placeholder}>
                  {commuter.employeeCorporateId
                    ? `#${commuter.employeeCorporateId}`
                    : ''}
                </Text>
              </NameContainer>
            </UserContainer>
          </TableRowCell>
          <TableRowCell onClick={() => onRowClick(commuter.id)}>
            {moment(commuter.archiveDate).format('MMMM Do, YYYY')}
          </TableRowCell>
          <TableRowCell onClick={() => onRowClick(commuter.id)}>
            {matchingWorksite ? matchingWorksite.alias : 'unknown'}
          </TableRowCell>
          <TableRowCell onClick={() => onRowClick(commuter.id)}>
            {commuter.email}
          </TableRowCell>
        </TableRow>
      )
    })
}

const formatMyCommuters = (employees: MyCommuterArchived[]): MyCommuter[] => {
  return employees.map((emp) => {
    return {
      id: emp.id,
      name: emp.name,
      email: emp.email,
      employeeCorporateId: emp.employeeCorporateId,
      worksiteId: emp.worksiteId,
      archived: emp.archived,
      archiveDate: emp.archiveDate,
      totalTrips: 0,
      programsEnrolled: [],
      segmentsEnrolled: [],
      competitionsEnrolled: [],
      hasPhysicalCard: false,
      remainingProgramBudget: null,
      currentIncentives: null,
      fleetRemaining: null,
      hasRegistered: false,
      hasInvite: false,
    }
  })
}

interface MyCommutersBlocked {
  blockedEmployees: MyCommuterArchived[]
}

const MyCommutersBlocked = React.memo((props: MyCommutersBlocked) => {
  const { blockedEmployees } = props

  const formattedEmployees = formatMyCommuters(blockedEmployees)

  const { worksites } = useSelector((state: AppState) => state.employer)
  const [sortByFilter] = useState('desc')
  const [filteredCommuters, setFilteredCommuters] = useState(formattedEmployees)
  const [activeEmployeeId, setActiveEmployeeId] = useState(null)

  useLayoutEffect(() => {
    setFilteredCommuters(formatMyCommuters(blockedEmployees))
  }, [blockedEmployees])

  const onRowClick = (id: string) => {
    setActiveEmployeeId(id)
  }

  if (!formattedEmployees || !filteredCommuters) {
    return <Loading isCard={false} />
  }

  return (
    <>
      <Body>
        <Table
          maxRows={10}
          headerCells={[
            <TableRowCell>Commuter</TableRowCell>,
            <TableRowCell>Blocked Date</TableRowCell>,
            <TableRowCell>Worksite</TableRowCell>,
            <TableRowCell>Email</TableRowCell>,
          ]}
          emptyMessage='No Results Found'
          rows={
            filteredCommuters.length > 0
              ? getTableRows(
                  filteredCommuters,
                  sortByFilter,
                  worksites,
                  onRowClick
                )
              : null
          }
        />
      </Body>
      <MyCommuterDetailsModal
        employeeId={activeEmployeeId}
        open={activeEmployeeId && true}
        closeModal={() => setActiveEmployeeId(null)}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommutersBlocked.displayName = 'MyCommutersBlocked'
}

export default MyCommutersBlocked
